import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest } from 'utils/api';

// ==============================|| INTERFACES ||============================== //

interface InsightReport {
  id: string;
  projectId: string;
  organizationId: string;
  reportType: string;
  audience: string;
  version: number;
  content: string | null;
  status: string;
  metadata: Record<string, any> | null;
  generatedAt: string | null;
  tokensUsed: number | null;
  acuCost: number | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

interface InsightsError {
  message: string;
  activeCount?: number;
  maxConcurrent?: number;
}

interface DeletedReportSummary {
  reportType: string;
  count: number;
  totalACU: number;
}

interface InsightsState {
  reports: InsightReport[];
  currentReport: InsightReport | null;
  deletedSummary: DeletedReportSummary[];
  status: 'idle' | 'loading' | 'success' | 'failed';
  generateStatus: 'idle' | 'loading' | 'success' | 'failed';
  error: InsightsError | null;
}

// ==============================|| INITIAL STATE ||============================== //

const initialState: InsightsState = {
  reports: [],
  currentReport: null,
  deletedSummary: [],
  status: 'idle',
  generateStatus: 'idle',
  error: null,
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getInsightReports = createAsyncThunk(
  'insights/getInsightReports',
  async ({ projectId, filters }: { projectId: string; filters?: { reportType?: string; audience?: string; status?: string } }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (filters?.reportType) params.append('reportType', filters.reportType);
      if (filters?.audience) params.append('audience', filters.audience);
      if (filters?.status) params.append('status', filters.status);
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await apiRequest(`/insights/projects/${projectId}/reports${query}`);
      return response;
    } catch (error: any) {
      console.error('Fetch insight reports error:', error);
      return rejectWithValue('Failed to fetch insight reports');
    }
  }
);

export const getInsightReport = createAsyncThunk(
  'insights/getInsightReport',
  async ({ projectId, reportId }: { projectId: string; reportId: string }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/insights/projects/${projectId}/reports/${reportId}`);
      return response;
    } catch (error: any) {
      console.error('Fetch insight report error:', error);
      return rejectWithValue('Failed to fetch insight report');
    }
  }
);

export const generateInsightReport = createAsyncThunk(
  'insights/generateInsightReport',
  async ({ projectId, reportType, audience }: { projectId: string; reportType: string; audience?: string }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/insights/projects/${projectId}/reports/generate`, {
        method: 'POST',
        body: JSON.stringify({ reportType, audience: audience || 'technical' }),
      });
      return response;
    } catch (error: any) {
      console.error('Generate insight report error:', error);
      // Pass structured error with capacity details if available
      const errorDetails = error?.error || error;
      return rejectWithValue({
        message: errorDetails?.message || error?.message || 'Failed to generate report',
        activeCount: errorDetails?.activeCount,
        maxConcurrent: errorDetails?.maxConcurrent,
      });
    }
  }
);

export const cancelInsightReport = createAsyncThunk(
  'insights/cancelInsightReport',
  async ({ projectId, reportId }: { projectId: string; reportId: string }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/insights/projects/${projectId}/reports/${reportId}/cancel`, {
        method: 'POST',
      });
      return response;
    } catch (error: any) {
      console.error('Cancel insight report error:', error);
      return rejectWithValue('Failed to cancel report');
    }
  }
);

export const deleteInsightReport = createAsyncThunk(
  'insights/deleteInsightReport',
  async ({ projectId, reportId }: { projectId: string; reportId: string }, { rejectWithValue }) => {
    try {
      await apiRequest(`/insights/projects/${projectId}/reports/${reportId}`, {
        method: 'DELETE',
      });
      return reportId;
    } catch (error: any) {
      console.error('Delete insight report error:', error);
      return rejectWithValue('Failed to delete report');
    }
  }
);

export const getDeletedReportsSummary = createAsyncThunk(
  'insights/getDeletedReportsSummary',
  async ({ projectId }: { projectId: string }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/insights/projects/${projectId}/reports/deleted-summary`);
      return response;
    } catch (error: any) {
      return rejectWithValue('Failed to fetch deleted reports summary');
    }
  }
);

// ==============================|| SLICE ||============================== //

export const insightsSlice = createSlice({
  name: 'insights',
  initialState,
  reducers: {
    clearInsightsError(state) {
      state.error = null;
    },
    clearCurrentReport(state) {
      state.currentReport = null;
    },
    clearInsightsState(state) {
      state.reports = [];
      state.currentReport = null;
      state.deletedSummary = [];
      state.status = 'idle';
      state.generateStatus = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // List reports
    builder
      .addCase(getInsightReports.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getInsightReports.fulfilled, (state, action) => {
        const data = action.payload?.response || action.payload;
        state.reports = Array.isArray(data) ? data : [];
        state.status = 'success';
        state.error = null;
      })
      .addCase(getInsightReports.rejected, (state, action) => {
        state.status = 'failed';
        state.error = { message: (action.payload as string) || 'Failed to fetch reports' };
      });

    // Get single report
    builder
      .addCase(getInsightReport.pending, (state) => {
        // Don't change main status — this is a detail fetch
      })
      .addCase(getInsightReport.fulfilled, (state, action) => {
        state.currentReport = action.payload?.response || action.payload;
        state.error = null;
      })
      .addCase(getInsightReport.rejected, (state, action) => {
        state.error = { message: (action.payload as string) || 'Failed to fetch report' };
      });

    // Generate report
    builder
      .addCase(generateInsightReport.pending, (state) => {
        state.generateStatus = 'loading';
      })
      .addCase(generateInsightReport.fulfilled, (state, action) => {
        const report = action.payload?.response || action.payload;
        if (report) {
          state.reports = [report, ...state.reports];
        }
        state.generateStatus = 'success';
        state.error = null;
      })
      .addCase(generateInsightReport.rejected, (state, action) => {
        state.generateStatus = 'failed';
        state.error = (action.payload as InsightsError) || { message: 'Failed to generate report' };
      });

    // Cancel report
    builder
      .addCase(cancelInsightReport.fulfilled, (state, action) => {
        const updated = action.payload?.response || action.payload;
        if (updated) {
          const index = state.reports.findIndex((r) => r.id === updated.id);
          if (index !== -1) {
            state.reports[index] = updated;
          }
        }
        state.error = null;
      })
      .addCase(cancelInsightReport.rejected, (state, action) => {
        state.error = { message: (action.payload as string) || 'Failed to cancel report' };
      });

    // Delete report
    builder
      .addCase(deleteInsightReport.fulfilled, (state, action) => {
        const reportId = action.payload;
        state.reports = state.reports.filter((r) => r.id !== reportId);
        state.error = null;
      })
      .addCase(deleteInsightReport.rejected, (state, action) => {
        state.error = { message: (action.payload as string) || 'Failed to delete report' };
      });

    // Deleted reports summary
    builder
      .addCase(getDeletedReportsSummary.fulfilled, (state, action) => {
        const data = action.payload?.response || action.payload;
        state.deletedSummary = Array.isArray(data) ? data : [];
      });
  },
});

// ==============================|| ACTIONS ||============================== //

export const { clearInsightsError, clearCurrentReport, clearInsightsState } = insightsSlice.actions;

// ==============================|| SELECTORS ||============================== //

export const selectInsightReports = (state: any) => state.insights.reports;
export const selectCurrentInsightReport = (state: any) => state.insights.currentReport;
export const selectInsightsStatus = (state: any) => state.insights.status;
export const selectInsightsGenerateStatus = (state: any) => state.insights.generateStatus;
export const selectInsightsError = (state: any) => state.insights.error;
export const selectDeletedReportsSummary = (state: any) => state.insights.deletedSummary;

export default insightsSlice.reducer;
